
// src/scripts/sqlApiHelper/tillsApi.js

import { auth, db } from "@/firebase";

import {
  doc,
  collection,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  serverTimestamp,
  writeBatch,
} from "firebase/firestore";

/* =========================
   HELPERS
   ========================= */

function must(v, msg) {
  if (!v) throw new Error(msg);
  return v;
}

function asNumber(n, fallback = 0) {
  const x = Number(n);
  return Number.isFinite(x) ? x : fallback;
}

function normLines(lines) {
  const arr = Array.isArray(lines) ? lines : [];

  return arr
    .map((l) => ({
      line_type: String(
        l?.line_type ?? l?.lineType ?? ""
      ).trim(),

      label: String(l?.label ?? "").trim(),

      amount: asNumber(l?.amount, 0),
    }))
    .filter(
      (l) =>
        l.line_type &&
        l.label &&
        Number.isFinite(l.amount)
    );
}

function currentUserMeta() {
  return {
    updated_by_uid:
      auth.currentUser?.uid ?? null,

    updated_by_email:
      auth.currentUser?.email ?? null,
  };
}

/* ==========================================
   SAVE / UPDATE TILLS
   ========================================== */

export async function upsertTillsForTradingDay(payload) {
  must(
    payload && typeof payload === "object",
    "Payload is required"
  );

  const venue_id = must(
    payload.venue_id,
    "Missing venue_id"
  );

  const week_ending_date = must(
    payload.week_ending_date,
    "Missing week_ending_date"
  );

  const trading_date = must(
    payload.trading_date,
    "Missing trading_date"
  );

  const tills = payload.tills;

  if (!Array.isArray(tills)) {
    throw new Error("tills must be an array");
  }

  const dayRef = doc(
    db,
    "venues",
    venue_id,
    "tills_days",
    trading_date
  );

  const batch = writeBatch(db);

  // DAY HEADER
  batch.set(
    dayRef,
    {
      venue_id,
      trading_date,
      week_ending_date,

      ...currentUserMeta(),

      updated_at: serverTimestamp(),
    },
    { merge: true }
  );

  // INDIVIDUAL TILLS
  for (let i = 0; i < tills.length; i++) {
    const t = tills[i] || {};

    const till_number = asNumber(
      t.till_number ?? t.tillNumber ?? i + 1
    );

    if (!Number.isFinite(till_number)) {
      throw new Error(
        "Invalid till_number in tills array"
      );
    }

    const declared_total = asNumber(
      t.declared_total ?? t.declaredTotal ?? 0
    );

    const counted_total = asNumber(
      t.counted_total ?? t.countedTotal ?? 0
    );

    const variance = asNumber(
      t.variance,
      counted_total - declared_total
    );

    const tillRef = doc(
      dayRef,
      "tills",
      String(till_number)
    );

    batch.set(
      tillRef,
      {
        till_number,
        declared_total,
        counted_total,
        variance,

        notes:
          t.notes != null
            ? String(t.notes)
            : null,

        // Includes cash, PDQ, Fatsoma,
        // Collins, X-Read and totals
        lines: normLines(t.lines),

        ...currentUserMeta(),

        updated_at: serverTimestamp(),
      },
      { merge: true }
    );
  }

  await batch.commit();

  return {
    ok: true,
    venue_id,
    week_ending_date,
    trading_date,

    rows: tills.map((t, i) => {
      const tn = asNumber(
        t?.till_number ?? t?.tillNumber ?? i + 1
      );

      const declared_total = asNumber(
        t?.declared_total ?? t?.declaredTotal ?? 0
      );

      const counted_total = asNumber(
        t?.counted_total ?? t?.countedTotal ?? 0
      );

      const lines = normLines(t?.lines);

      return {
        id: `${venue_id}:${trading_date}:${tn}`,

        venue_id,
        week_ending_date,
        trading_date,

        till_number: tn,
        declared_total,
        counted_total,

        variance: asNumber(
          t?.variance,
          counted_total - declared_total
        ),

        notes: t?.notes ?? null,

        lines,

        till_count_lines: lines,
      };
    }),
  };
}

/* ==========================================
   LOAD TILLS FOR TRADING DAY
   ========================================== */

export async function getTillsForTradingDay({
  venue_id,
  trading_date,
  week_ending_date,
}) {
  must(venue_id, "Missing venue_id");
  must(trading_date, "Missing trading_date");

  const dayRef = doc(
    db,
    "venues",
    venue_id,
    "tills_days",
    trading_date
  );

  const daySnap = await getDoc(dayRef);

  if (!daySnap.exists()) {
    return {
      ok: true,
      venue_id,
      trading_date,
      rows: [],
    };
  }

  const day = daySnap.data() || {};

  const we =
    day.week_ending_date ??
    week_ending_date ??
    null;

  const tillsCol = collection(
    dayRef,
    "tills"
  );

  const qy = query(
    tillsCol,
    orderBy("till_number", "asc")
  );

  const snap = await getDocs(qy);

  const rows = snap.docs.map((d) => {
    const t = d.data() || {};

    const lines = Array.isArray(t.lines)
      ? t.lines
      : [];

    return {
      id: `${venue_id}:${trading_date}:${d.id}`,

      venue_id,

      week_ending_date: we,

      trading_date,

      till_number: asNumber(
        t.till_number,
        asNumber(d.id)
      ),

      declared_total: asNumber(
        t.declared_total,
        0
      ),

      counted_total: asNumber(
        t.counted_total,
        0
      ),

      variance: asNumber(
        t.variance,
        asNumber(t.counted_total, 0) -
          asNumber(t.declared_total, 0)
      ),

      notes: t.notes ?? null,

      lines,

      till_count_lines: lines,
    };
  });

  return {
    ok: true,
    venue_id,
    trading_date,
    rows,
  };
}

/* ==========================================
   LOAD TILLS FOR DATE RANGE
   ========================================== */

export async function getTillsForDateRange({
  venue_id,
  date_from,
  date_to,
}) {
  must(venue_id, "Missing venue_id");
  must(date_from, "Missing date_from");
  must(date_to, "Missing date_to");

  const daysCol = collection(
    db,
    "venues",
    venue_id,
    "tills_days"
  );

  const qDays = query(
    daysCol,
    where(
      "trading_date",
      ">=",
      date_from
    ),
    where(
      "trading_date",
      "<=",
      date_to
    ),
    orderBy("trading_date", "asc")
  );

  const daySnaps = await getDocs(qDays);

  const rows = [];

  for (const dayDoc of daySnaps.docs) {
    const day = dayDoc.data() || {};

    const trading_date =
      day.trading_date ?? dayDoc.id;

    const week_ending_date =
      day.week_ending_date ?? null;

    const tillsCol = collection(
      dayDoc.ref,
      "tills"
    );

    const qTills = query(
      tillsCol,
      orderBy("till_number", "asc")
    );

    const tillsSnap = await getDocs(qTills);

    for (const tDoc of tillsSnap.docs) {
      const t = tDoc.data() || {};

      const lines = Array.isArray(t.lines)
        ? t.lines
        : [];

      rows.push({
        id: `${venue_id}:${trading_date}:${tDoc.id}`,

        venue_id,
        week_ending_date,
        trading_date,

        till_number: asNumber(
          t.till_number,
          asNumber(tDoc.id)
        ),

        declared_total: asNumber(
          t.declared_total,
          0
        ),

        counted_total: asNumber(
          t.counted_total,
          0
        ),

        variance: asNumber(
          t.variance,

          asNumber(t.counted_total, 0) -
            asNumber(t.declared_total, 0)
        ),

        notes: t.notes ?? null,

        till_count_lines: lines,

        lines,
      });
    }
  }

  return {
    ok: true,
    venue_id,
    date_from,
    date_to,
    rows,
  };
}

/* ==========================================
   LOAD WEEKLY TILL SUMMARIES
   ========================================== */

export async function getTillWeekSummariesForRange({
  venueId,
  date_from,
  date_to,
}) {
  must(venueId, "Missing venueId");
  must(date_from, "Missing date_from");
  must(date_to, "Missing date_to");

  const colRef = collection(
    db,
    "venues",
    venueId,
    "till_week_summaries"
  );

  const qy = query(
    colRef,

    where(
      "week_ending_date",
      ">=",
      date_from
    ),

    where(
      "week_ending_date",
      "<=",
      date_to
    ),

    orderBy(
      "week_ending_date",
      "asc"
    )
  );

  const snap = await getDocs(qy);

  const rows = snap.docs.map((d) => {
    const x = d.data() || {};

    return {
      week_ending_date:
        x.week_ending_date ?? d.id,

      payload: x.payload ?? null,
    };
  });

  // Retain existing debugging behaviour
  try {
    const r = Array.isArray(rows)
      ? rows
      : [];

    console.log(
      "[till-week-range-get] ok:",
      true,
      "rows:",
      r.length
    );

    if (r.length) {
      console.log(
        "[till-week-range-get] first.week_ending_date:",
        r[0]?.week_ending_date
      );

      console.log(
        "[till-week-range-get] first.payload:",
        r[0]?.payload
      );

      for (const one of r) {
        console.log(
          "[till-week-range-get] week:",
          one?.week_ending_date,
          "payload:",
          one?.payload
        );
      }
    } else {
      console.log(
        "[till-week-range-get] no rows returned"
      );
    }
  } catch (e) {
    console.log(
      "[till-week-range-get] debug logging failed:",
      e?.message || e
    );
  }

  return {
    ok: true,
    venueId,
    date_from,
    date_to,
    rows,
  };
}
